<template>
  <section class="testimonials-section section">
    <div class="container">
      <!-- Header -->
      <div class="section-header text-center">
        <h2
          class="section-header__title"
          v-html="resolvedTitle" />
      </div>

      <!-- Carousel wrapper -->
      <div
        class="carousel"
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"
        @touchstart.passive="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend="onTouchEnd">
        <div class="carousel__track-wrapper">
          <div
            class="carousel__track"
            :style="trackStyle">
            <div
              v-for="(item, index) in items"
              :key="index"
              class="carousel__slide"
              :class="{
                'carousel__slide--active': isActive(index),
                'carousel__slide--visible': isVisible(index),
              }">
              <slot
                :item="item"
                :index="index"
                :active="isActive(index)">
                <!-- Default card rendering si pas de slot -->
                <div class="testimonial-card">
                  <div class="testimonial-card__border-top" />
                  <div class="testimonial-card__body">
                    <p
                      v-for="(para, i) in item.paragraphs"
                      :key="i"
                      class="testimonial-card__text">
                      {{ para }}
                    </p>
                  </div>
                  <div class="testimonial-card__author">
                    <div class="testimonial-card__avatar">
                      <img
                        v-if="item.avatar"
                        :src="item.avatar"
                        :alt="item.name"
                        class="testimonial-card__avatar-img"
                        width="72"
                        height="72"
                        loading="lazy"
                        decoding="async" />
                      <span
                        v-else
                        class="testimonial-card__avatar-placeholder">
                        {{ item.initials }}
                      </span>
                    </div>
                    <p class="testimonial-card__name">{{ item.name }}</p>
                    <p class="testimonial-card__role">{{ item.role }}</p>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </div>

        <!-- Arrows (desktop) -->
        <button
          class="carousel__arrow carousel__arrow--prev"
          :disabled="canGoPrev ? undefined : true"
          aria-label="Précédent"
          @click="prev">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none">
            <path
              d="M12 4l-6 6 6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <button
          class="carousel__arrow carousel__arrow--next"
          :disabled="canGoNext ? undefined : true"
          aria-label="Suivant"
          @click="next">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none">
            <path
              d="M8 4l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- Dots -->
      <div
        class="carousel__dots"
        role="tablist">
        <button
          v-for="(_, index) in totalDots"
          :key="index"
          class="carousel__dot"
          :class="{ 'carousel__dot--active': index === currentIndex }"
          :aria-label="`Slide ${index + 1}`"
          :aria-selected="index === currentIndex"
          role="tab"
          @click="goTo(index)" />
      </div>

      <!-- CTA -->
      <div class="testimonials-section__cta text-center">
        <NuxtLink
          to="/notre-mission"
          class="btn btn-primary btn-lg">
          {{ t('testimonials.cta') }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { ComputedRef } from 'vue'

const { t } = useI18n()

type TestimonialItem = {
  paragraphs: string[]
  avatar: string
  name: string
  role: string
  initials: string
}

// ── Props ─────────────────────────────────────────────────
const props = defineProps({
  items: {
    type: Array as PropType<TestimonialItem[]>,
    required: true,
  },
  title: {
    type: String,
    default: null, // null = utilise la clé i18n
  },
  /** Nombre de slides visibles simultanément sur desktop */
  visibleDesktop: {
    type: Number,
    default: 2,
  },
  /** Nombre de slides visibles simultanément sur mobile */
  visibleMobile: {
    type: Number,
    default: 1,
  },
  /** Autoplay en ms (0 = désactivé) */
  autoplayDelay: {
    type: Number,
    default: 5000,
  },
})

// ── Titre résolu (prop ou i18n)
const resolvedTitle = computed(() => props.title ?? t('testimonials.title'))

// ── State ─────────────────────────────────────────────────
const currentIndex = ref(0)
const isMobile = ref(false)
const isPaused = ref(false)

// ── Computed ──────────────────────────────────────────────
const slidesVisible = computed(() => (isMobile.value ? props.visibleMobile : props.visibleDesktop))

const totalDots = computed(() => props.items.length - slidesVisible.value + 1)

const canGoPrev: ComputedRef<boolean> = computed(() => currentIndex.value > 0)
const canGoNext: ComputedRef<boolean> = computed(() => currentIndex.value < totalDots.value - 1)

/** Largeur d'un slide en % du wrapper */
const slideWidth = computed(() => 100 / slidesVisible.value)

const trackStyle = computed(() => ({
  transform: `translateX(-${currentIndex.value * slideWidth.value}%)`,
  transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
}))

// ── Helpers ───────────────────────────────────────────────
function isActive(index: number): boolean {
  return index === currentIndex.value
}

function isVisible(index: number): boolean {
  return index >= currentIndex.value && index < currentIndex.value + slidesVisible.value
}

// ── Navigation ────────────────────────────────────────────
function goTo(index: number): void {
  currentIndex.value = Math.max(0, Math.min(index, totalDots.value - 1))
}

function prev(): void {
  goTo(currentIndex.value - 1)
}
function next(): void {
  goTo(currentIndex.value + 1)
}

// ── Autoplay ──────────────────────────────────────────────
let autoplayTimer: NodeJS.Timeout | null = null

function startAutoplay(): void {
  if (!props.autoplayDelay) return
  autoplayTimer = setInterval(() => {
    if (isPaused.value) return
    if (canGoNext.value) {
      next()
    } else {
      goTo(0)
    }
  }, props.autoplayDelay)
}

function pauseAutoplay(): void {
  isPaused.value = true
}
function resumeAutoplay(): void {
  isPaused.value = false
}

// ── Touch / Swipe ─────────────────────────────────────────
let touchStartX = 0
let touchDeltaX = 0

function onTouchStart(e: TouchEvent): void {
  touchStartX = e.touches[0]?.clientX ?? 0
  touchDeltaX = 0
  pauseAutoplay()
}

function onTouchMove(e: TouchEvent): void {
  touchDeltaX = (e.touches[0]?.clientX ?? 0) - touchStartX
}

function onTouchEnd(): void {
  if (touchDeltaX < -50) next()
  else if (touchDeltaX > 50) prev()
  resumeAutoplay()
}

// ── Responsive ────────────────────────────────────────────
function checkMobile(): void {
  isMobile.value = window.innerWidth < 768
  // Recadrer si l'index dépasse le nouveau max
  if (currentIndex.value >= totalDots.value) {
    currentIndex.value = Math.max(0, totalDots.value - 1)
  }
}

// ── Lifecycle ─────────────────────────────────────────────
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile, { passive: true })
  startAutoplay()
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  if (autoplayTimer) {
    clearInterval(autoplayTimer)
  }
})
</script>

<style scoped>
/* ── Section ──────────────────────────────────────────────── */
.testimonials-section {
  overflow: hidden;
}

.section-header {
  margin-bottom: var(--space-12);
}

.section-header__title {
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 800;
  letter-spacing: var(--tracking-tight);
  line-height: var(--leading-tight);
  margin-top: var(--space-3);
}

/* ── Carousel wrapper ─────────────────────────────────────── */
.carousel {
  position: relative;
  padding: 0 var(--space-12); /* espace pour les flèches */
}

.carousel__track-wrapper {
  overflow: hidden;
  border-radius: var(--radius-lg);
}

.carousel__track {
  display: flex;
  will-change: transform;
}

/* ── Slide ────────────────────────────────────────────────── */
.carousel__slide {
  flex: 0 0 50%; /* 2 visibles sur desktop */
  min-width: 0;
  padding: 0 var(--space-4);
  box-sizing: border-box;
  transition: opacity var(--duration-base) var(--ease-out);
}

/* Slides non-visibles un peu estompés sur desktop */
.carousel__slide:not(.carousel__slide--visible) {
  opacity: 0.3;
  pointer-events: none;
}

/* ── Testimonial Card ─────────────────────────────────────── */
.testimonial-card {
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

/* Barre top dégradée bleue → orange (signature visuelle des cards) */
.testimonial-card__border-top {
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--accent-blue) 0%,
    var(--accent-blue-dk) 50%,
    var(--accent-orange) 100%
  );
  flex-shrink: 0;
}

.testimonial-card__body {
  padding: var(--space-6) var(--space-6) var(--space-4);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.testimonial-card__text {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

/* ── Author block ─────────────────────────────────────────── */
.testimonial-card__author {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-4) var(--space-6) var(--space-6);
  gap: var(--space-2);
}

.testimonial-card__avatar {
  width: 72px;
  height: 72px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 2px solid transparent;
  background:
    linear-gradient(var(--bg-elevated), var(--bg-elevated)) padding-box,
    linear-gradient(135deg, var(--accent-blue), var(--accent-violet), var(--accent-orange))
      border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.testimonial-card__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: calc(var(--radius-lg) - 2px);
}

.testimonial-card__avatar-placeholder {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: var(--text-lg);
  color: var(--accent-blue-lt);
}

.testimonial-card__name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-base);
  color: var(--text-primary);
  margin-top: var(--space-1);
}

.testimonial-card__role {
  font-size: var(--text-sm);
  color: var(--accent-blue-lt);
}

/* ── Arrows ───────────────────────────────────────────────── */
.carousel__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
  z-index: 2;
}

.carousel__arrow:hover:not(:disabled) {
  color: var(--text-primary);
  border-color: var(--accent-blue);
  background: rgba(3, 160, 241, 0.1);
}

.carousel__arrow:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.carousel__arrow--prev {
  left: 0;
}
.carousel__arrow--next {
  right: 0;
}

/* ── Dots ─────────────────────────────────────────────────── */
.carousel__dots {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-8);
}

.carousel__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  cursor: pointer;
  transition:
    background var(--duration-base) var(--ease-out),
    width var(--duration-base) var(--ease-out);
  padding: 0;
}

.carousel__dot--active {
  background: var(--accent-blue);
  width: 24px;
}

/* ── CTA ──────────────────────────────────────────────────── */
.testimonials-section__cta {
  margin-top: var(--space-10);
}

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 768px) {
  .section-header__title {
    font-size: var(--text-2xl);
  }

  .carousel {
    padding: 0; /* pas de flèches sur mobile, swipe only */
  }

  .carousel__slide {
    flex: 0 0 100%;
    padding: 0 var(--space-2);
  }

  /* Sur mobile tous les slides non-actifs sont cachés */
  .carousel__slide:not(.carousel__slide--active) {
    opacity: 0;
  }

  .carousel__arrow {
    display: none;
  }
}
</style>
